/**
 * ExploreActions Component
 * Action buttons for constellation exploration
 */

import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useExploreStore } from '../../stores/explore-store';
import { generateExplore } from '../../services/explore.service';
import SaveConstellationModal from './SaveConstellationModal';
import { wanderingModal, WanderingModal } from '../WanderingModal';

interface ExploreActionsProps {
  onGenerate?: () => void;
}

export default function ExploreActions({ onGenerate }: ExploreActionsProps) {
  const {
    nodes,
    vibe,
    results,
    loading,
    isValid,
    setLoading,
    setError,
    setExploreResults,
    clearConstellation,
    clearResults,
  } = useExploreStore();

  const [showSaveModal, setShowSaveModal] = useState(false);
  const hasResults = results.length > 0;

  /**
   * Handle action with specified type
   */
  const handleAction = async (action: string, label: string) => {
    if (!isValid()) {
      toast.error('Add at least 3 nodes to generate recommendations');
      return;
    }

    wanderingModal.show();
    setLoading(true);
    setError(null);

    try {
      const response = await generateExplore({
        nodes,
        vibe,
        action,
        search_depth: 'balanced',
        previous_results: results.map((r) => r.id),
      });

      if (response.success) {
        setExploreResults(response);
        toast.success(`${label}! Generated ${response.data.recommendations.length} recommendations.`);
        if (onGenerate) onGenerate();
      } else {
        throw new Error(response.error || `Failed to ${label.toLowerCase()}`);
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error?.message || err?.message || 'An error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      wanderingModal.hide();
    }
  };

  const handleGenerate = () => handleAction('generate', 'Generated');
  const handleMakeWeirder = () => handleAction('make_weirder', 'Made weirder');
  const handleTighten = () => handleAction('tighten', 'Tightened coherence');
  const handleDeepDiveLabel = () => handleAction('deep_dive_label', 'Deep dive: Labels');
  const handleDeepDiveScene = () => handleAction('deep_dive_scene', 'Deep dive: Scenes');

  const handleBranch = () => {
    // Branch creates a copy of current constellation for A/B exploration
    // Keeps current nodes but clears results so user can modify and regenerate
    clearResults();
    toast.success('Constellation branched! Modify nodes or vibe and generate again to compare.');
  };

  const handleSave = () => {
    if (!isValid()) {
      toast.error('Add at least 3 nodes to save your constellation');
      return;
    }
    setShowSaveModal(true);
  };

  const handleClear = () => {
    clearConstellation();
    toast.success('Constellation cleared');
  };

  return (
    <div className="space-y-3">
      {/* Primary Action: Generate */}
      <button
        onClick={handleGenerate}
        disabled={!isValid() || loading}
        className="w-full px-6 py-4 bg-blue-600 text-white text-lg font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md relative overflow-hidden"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-3">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Generating...
          </span>
        ) : (
          'Generate Recommendations'
        )}
      </button>

      {/* Secondary Actions - Only show after first generation */}
      {hasResults && (
        <>
          <div className="pt-2 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-2 text-center">Refine your results</p>
          </div>

          {/* Refinement Actions */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleMakeWeirder}
              disabled={loading}
              className="px-3 py-2 bg-orange-50 text-orange-700 text-sm font-medium rounded-md hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-orange-200"
            >
              🌀 Make Weirder
            </button>
            <button
              onClick={handleTighten}
              disabled={loading}
              className="px-3 py-2 bg-green-50 text-green-700 text-sm font-medium rounded-md hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-green-200"
            >
              🎯 Tighten
            </button>
          </div>

          {/* Deep Dive Actions */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleDeepDiveLabel}
              disabled={loading}
              className="px-3 py-2 bg-purple-50 text-purple-700 text-sm font-medium rounded-md hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-purple-200"
            >
              🏷️ Deep Dive: Labels
            </button>
            <button
              onClick={handleDeepDiveScene}
              disabled={loading}
              className="px-3 py-2 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-md hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-indigo-200"
            >
              🌍 Deep Dive: Scenes
            </button>
          </div>

          {/* Utility Actions */}
          <div className="pt-2 border-t border-gray-200">
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={handleBranch}
                disabled={loading}
                className="px-2 py-2 bg-gray-50 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-gray-200"
              >
                🌿 Branch
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-2 py-2 bg-gray-50 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-gray-200"
              >
                💾 Save
              </button>
              <button
                onClick={handleClear}
                disabled={loading}
                className="px-2 py-2 bg-red-50 text-red-700 text-xs font-medium rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-red-200"
              >
                🗑️ Clear
              </button>
            </div>
          </div>
        </>
      )}

      {/* Save Constellation Modal */}
      <SaveConstellationModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
      />

      <WanderingModal allowDismiss onDismiss={() => { setLoading(false); wanderingModal.hide(); }} />
    </div>
  );
}
